/**
 * MorningPulse Component
 * 
 * "Good morning. 47 workers active in the Permian right now."
 * 
 * The first thing you see - a reminder you're not alone out here.
 * Shows active workers, hazards, weather, and community stats.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Chip,
  Collapse,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  WbSunny as SunIcon,
  NightsStay as MoonIcon,
  People as PeopleIcon,
  Warning as HazardIcon,
  CheckCircle as SafeIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { telegram } from '../services/telegram';
import { useTranslation } from '../i18n/translations';

interface BasinPulse {
  basin: {
    id: string;
    name: string;
  };
  pulse: {
    greeting: string;
    message: string;
    activeWorkers: number;
    safeReturnsToday: number;
    hazardCount: number;
  };
  hazards: Array<{
    id: string;
    type: string;
    typeLabel: string;
    typeEmoji: string;
    latitude: number;
    longitude: number;
    confirmations: number;
    timeAgo: string;
  }>;
  user: {
    badge: string;
    reports: number;
    confirmations: number;
    safeReturns: number;
  } | null;
}

interface MorningPulseProps {
  basin?: string;
  onNavigateToHazard?: (lat: number, lng: number) => void;
  compact?: boolean;
}

export const MorningPulse: React.FC<MorningPulseProps> = ({
  basin = 'permian',
  onNavigateToHazard,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [pulse, setPulse] = useState<BasinPulse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState<string | null>(null);

  // Fetch basin pulse data
  const fetchPulse = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/basin/${basin}/feed`, {
        headers: {
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPulse(data);
        
        // Check if user already checked in today
        if (data.user?.hasCheckedInToday) {
          setCheckedIn(true);
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || `Failed to load (${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching pulse:', error);
      setError('Network error - please check your connection');
    } finally {
      setLoading(false);
    }
  }, [basin]);

  // Initial fetch
  useEffect(() => {
    fetchPulse();
    
    // Refresh every 2 minutes
    const interval = setInterval(fetchPulse, 120000);
    return () => clearInterval(interval);
  }, [fetchPulse]);

  // Handle "Made it home" check-in
  const handleSafeReturn = async () => {
    setCheckingIn(true);
    try {
      const response = await fetch('/api/checkin/safe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
        body: JSON.stringify({ basin }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCheckedIn(true);
        setCheckInMessage(data.message);
        
        // Refresh pulse to update stats
        setTimeout(fetchPulse, 1000);
      }
    } catch (error) {
      console.error('Error checking in:', error);
    } finally {
      setCheckingIn(false);
    }
  };

  // Get time-based icon
  const getTimeIcon = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 18) {
      return <SunIcon sx={{ color: '#fbbf24' }} />;
    }
    return <MoonIcon sx={{ color: '#818cf8' }} />;
  };

  // Get badge emoji
  const getBadgeEmoji = (badge: string) => {
    switch (badge) {
      case 'guardian': return '🛡️';
      case 'trusted': return '⭐';
      case 'verified': return '✓';
      case 'active': return '👷';
      default: return '🆕';
    }
  };

  if (loading && !pulse) {
    return (
      <Card
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
          border: '1px solid rgba(96, 165, 250, 0.2)',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: '#60a5fa' }} />
          <Typography sx={{ ml: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            {t('loadingBasinPulse')}
          </Typography>
        </Box>
      </Card>
    );
  }

  if (error || !pulse) {
    return (
      <Card
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: '#ef4444', fontWeight: 600, fontSize: '0.95rem', mb: 1 }}>
          ⚠️ {t('unableToLoadPulse')}
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', mb: 2 }}>
          {error || t('noDataAvailable')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={fetchPulse}
          sx={{ 
            color: '#60a5fa', 
            borderColor: 'rgba(96, 165, 250, 0.5)',
            '&:hover': { borderColor: '#60a5fa' }
          }}
        >
          {t('tryAgain')}
        </Button>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        border: '1px solid rgba(96, 165, 250, 0.3)',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 0 20px rgba(96, 165, 250, 0.1)',
      }}
    >
      {/* Header - Always visible */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: compact ? 'pointer' : 'default',
          '&:hover': compact ? { background: 'rgba(255, 255, 255, 0.02)' } : {},
        }}
        onClick={compact ? () => setExpanded(!expanded) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getTimeIcon()}
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
              {pulse.pulse.greeting}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              {pulse.pulse.message}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); fetchPulse(); }}>
            <RefreshIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} />
          </IconButton>
          {compact && (
            <IconButton size="small">
              {expanded ? (
                <CollapseIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              ) : (
                <ExpandIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Stats Row */}
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            icon={<PeopleIcon sx={{ color: '#60a5fa !important', fontSize: 16 }} />}
            label={`${pulse.pulse.activeWorkers} ${t('activeCount')}`}
            size="small"
            sx={{
              background: 'rgba(96, 165, 250, 0.15)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              color: '#60a5fa',
              '& .MuiChip-label': { fontSize: '0.75rem' },
            }}
          />
          
          {pulse.pulse.hazardCount > 0 && (
            <Chip
              icon={<HazardIcon sx={{ color: '#f59e0b !important', fontSize: 16 }} />}
              label={`${pulse.pulse.hazardCount} ${pulse.pulse.hazardCount === 1 ? t('hazard') : t('hazards')}`}
              size="small"
              sx={{
                background: 'rgba(245, 158, 11, 0.15)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                color: '#f59e0b',
                '& .MuiChip-label': { fontSize: '0.75rem' },
              }}
            />
          )}
          
          <Chip
            icon={<SafeIcon sx={{ color: '#22c55e !important', fontSize: 16 }} />}
            label={`${pulse.pulse.safeReturnsToday} ${t('homeSafe')}`}
            size="small"
            sx={{
              background: 'rgba(34, 197, 94, 0.15)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              color: '#22c55e',
              '& .MuiChip-label': { fontSize: '0.75rem' },
            }}
          />
        </Box>

        {/* Active Hazards Preview */}
        {pulse.hazards.length > 0 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('activeHazards')}
            </Typography>
            {pulse.hazards.slice(0, 3).map((hazard) => (
              <Box
                key={hazard.id}
                onClick={() => onNavigateToHazard?.(hazard.latitude, hazard.longitude)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  mb: 0.5,
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 1,
                  cursor: onNavigateToHazard ? 'pointer' : 'default',
                  '&:hover': onNavigateToHazard ? { background: 'rgba(255, 255, 255, 0.06)' } : {},
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1rem' }}>{hazard.typeEmoji}</Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.8rem' }}>
                    {hazard.typeLabel}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {hazard.confirmations > 0 && (
                    <Typography sx={{ color: '#22c55e', fontSize: '0.7rem' }}>
                      ✓ {hazard.confirmations}
                    </Typography>
                  )}
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.7rem' }}>
                    {hazard.timeAgo}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* User Badge & Safe Return */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(96, 165, 250, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {pulse.user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>
                {getBadgeEmoji(pulse.user.badge)}
              </Typography>
              <Box>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'capitalize' }}>
                  {pulse.user.badge} {t('worker')}
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem' }}>
                  {pulse.user.reports} {t('reports')} • {pulse.user.confirmations} {t('confirms')}
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Made it home button */}
          {checkedIn ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SafeIcon sx={{ color: '#22c55e', fontSize: 18 }} />
              <Typography sx={{ color: '#22c55e', fontSize: '0.8rem' }}>
                {checkInMessage || t('homeSafeCheck')}
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="small"
              disabled={checkingIn}
              onClick={handleSafeReturn}
              startIcon={checkingIn ? <CircularProgress size={14} /> : <HomeIcon />}
              sx={{
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                '&:hover': { background: 'linear-gradient(135deg, #4ade80, #22c55e)' },
                fontSize: '0.75rem',
                py: 0.75,
                px: 2,
              }}
            >
              {t('madeItHome')}
            </Button>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

export default MorningPulse;
